import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { IEmployeeBaseSalaryRange, IEmployeeItem, IEmployeeListItem } from 'src/types/employee';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  employees: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    employees: IEmployeeListItem[];
  };
  employee: IEmployeeItem | null;
  baseSalary: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    data: IEmployeeBaseSalaryRange[];
  };
  employeeNumber: string | null;
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  employees: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    employees: [],
  },
  employee: null,
  baseSalary: {
    status: 'idle',
    error: undefined,
    data: [],
  },
  employeeNumber: null,
};

const employeesSlice = createSlice({
  name: 'employees',
  initialState,
  reducers: {
    removeEmployee: (state, action) => {
      const { employeeId } = action.payload;

      const index = state.employees.employees.findIndex((employee) => employee._id === employeeId);

      if (index > -1) {
        action.payload.index = index;
        state.employees.employees = state.employees.employees.filter(
          (employee) => employee._id !== employeeId
        );
      }
    },
    rollbackEmployeeDeletion: (state, action) => {
      const { employee, index } = action.payload;

      state.employees.employees.splice(index, 0, employee);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaginatedEmployees.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPaginatedEmployees.fulfilled, (state, action) => {
        state.status = 'success';
        state.employees = action.payload;
      })
      .addCase(getPaginatedEmployees.rejected, (state, action) => {
        state.employees = initialState.employees;
        state.status = 'failure';
        state.error = action.error.message;
      })
      // Get User By ID
      .addCase(getEmployeeById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getEmployeeById.fulfilled, (state, action) => {
        state.status = 'success';
        state.employee = action.payload;
        state.error = initialState.error;
      })
      .addCase(getEmployeeById.rejected, (state, action) => {
        state.employee = initialState.employee;
        state.status = 'failure';
        state.error = action.error.message;
      })

      .addCase(endEmployment.fulfilled, (state, action) => {
        state.employee = action.payload;
      })

      .addCase(getBaseSalaryRange.pending, (state) => {
        state.baseSalary.status = 'loading';
      })
      .addCase(getBaseSalaryRange.fulfilled, (state, action) => {
        state.baseSalary.status = 'success';
        state.baseSalary.data = action.payload;
      })
      .addCase(getBaseSalaryRange.rejected, (state, action) => {
        state.baseSalary.status = 'failure';
        state.baseSalary.error = action.error.message;
      })

      .addCase(getNextAvaulableEmployeeNumber.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getNextAvaulableEmployeeNumber.fulfilled, (state, action) => {
        state.status = 'success';
        state.employeeNumber = action.payload;
      })
      .addCase(getNextAvaulableEmployeeNumber.rejected, (state, action) => {
        state.status = 'failure';
        state.employeeNumber = initialState.employeeNumber;
        state.error = action.error.message;
      });
  },
});

export default employeesSlice.reducer;

export const { removeEmployee, rollbackEmployeeDeletion } = employeesSlice.actions;

export const getBaseSalaryRange = createAsyncThunk('employees/getBaseSalaryRange', async () => {
  try {
    const response = await axios.get(`employee/salary-range`);

    return response.data.data;
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const getPaginatedEmployees = createAsyncThunk(
  'employees/paginated',
  async (params: {
    page: number | null;
    limit: number | null;
    generalSearch: string | null;
    employeeNumber?: string | null;
    epf?: boolean | null;
    baseSalary?: number | null;
    firstName?: string | null;
    sortBy: string | null;
    sort: 'asc' | 'desc' | null;
    status: string;
  }) => {
    try {
      const response = await axios.get(
        `employee?page=${params.page !== null ? params.page + 1 : ''}&limit=${
          params.limit ?? ''
        }&generalSearch=${params.generalSearch ?? ''}&employeeNumber=${
          params.employeeNumber ?? ''
        }&epf=${params.epf ?? ''}&firstName=${params.firstName ?? ''}&baseSalary=${
          params.baseSalary ?? ''
        }&status=${params.status}&sortBy=${params.sortBy}&sort=${params.sort}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getEmployeeById = createAsyncThunk(
  'employees/getEmployeeById',
  async (params: { employeeId: string }) => {
    const { employeeId } = params;

    try {
      const response = await axios.get(`employee/${employeeId}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getNextAvaulableEmployeeNumber = createAsyncThunk(
  'employees/employeeNumber',
  async () => {
    try {
      const response = await axios.get(`employee/next-available-employee-number`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deleteEmployee = createAsyncThunk(
  'employees/deleteEmployee',
  async (params: { employee: IEmployeeListItem; index: number }, { dispatch }) => {
    const { employee, index } = params;

    try {
      const response = await axios.delete(`employee/${employee._id}`);
      return response.data.data;
    } catch (err) {
      dispatch(rollbackEmployeeDeletion({ employee, index }));
      throw err;
    }
  }
);

export interface TCreateEmployee
  extends Omit<
    IEmployeeItem,
    | '_id'
    | 'createdAt'
    | 'updatedAt'
    | 'status'
    | 'nicFrontImage'
    | 'nicBackImage'
    | 'birthDate'
    | 'joinedAt'
  > {
  nicFrontImage?: File | string | null;
  nicBackImage?: File | string | null;
  birthDate?: Date;
  joinedAt?: Date;
}

function buildEmployeeFormData(employee: TCreateEmployee): FormData {
  const formData = new FormData();

  Object.entries(employee).forEach(([key, value]) => {
    if (key === 'nicFrontImage' || key === 'nicBackImage') return;
    if (value === undefined || value === null) return;

    if (key === 'bankDetails') {
      formData.append('bankDetails', JSON.stringify(value));
      return;
    }

    if (value instanceof Date) {
      formData.append(key, value.toISOString());
      return;
    }

    formData.append(key, value as string);
  });

  if (employee.nicFrontImage instanceof File) {
    formData.append('nicFrontImage', employee.nicFrontImage);
  } else if (employee.nicFrontImage === null) {
    formData.append('removeNicFrontImage', 'true');
  }

  if (employee.nicBackImage instanceof File) {
    formData.append('nicBackImage', employee.nicBackImage);
  } else if (employee.nicBackImage === null) {
    formData.append('removeNicBackImage', 'true');
  }

  return formData;
}

export const createEmpployee = createAsyncThunk(
  'employees/createEmpployee',
  async (params: { employee: TCreateEmployee }) => {
    const { employee } = params;

    const response = await axios.post(`employee`, buildEmployeeFormData(employee));

    return response.data.data;
  }
);

export const updateEmployee = createAsyncThunk(
  'employees/updateEmployee',
  async (params: { employeeId: string; employee: TCreateEmployee }) => {
    const { employee, employeeId } = params;
    const response = await axios.patch(`/employee/${employeeId}`, buildEmployeeFormData(employee));
    return response.data.data;
  }
);

export const endEmployment = createAsyncThunk(
  'employees/endEmployment',
  async (params: { employeeId: string; employmentEndAt: Date; employmentEndReason: string }) => {
    const { employeeId, ...body } = params;
    const response = await axios.patch(`/employee/${employeeId}/end-employment`, body);
    return response.data.data;
  }
);
